import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, Pencil, Save, X } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const emptyForm = { name: "", contact_name: "", email: "", phone: "", address: "", account_number: "" };

function DirectoryFields({ kind, form, setForm, prefix = "" }) {
  const isSupplier = kind === "supplier";
  const p = prefix ? `${prefix}-` : "";
  return (
    <>
      <div className="space-y-2">
        <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Name *</Label>
        <Input data-testid={`${p}${kind}-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-10 rounded-none border-2" />
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Contact name</Label>
        <Input data-testid={`${p}${kind}-contact`} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="h-10 rounded-none border-2" />
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] uppercase tracking-[0.2em] font-bold whitespace-nowrap">Email * · comma-separate for multiple</Label>
        <Input data-testid={`${p}${kind}-email`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="ops@courier.com, dispatch@courier.com" className="h-10 rounded-none border-2 font-mono text-xs" />
      </div>
      <div className="space-y-2">
        <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Phone</Label>
        <Input data-testid={`${p}${kind}-phone`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10 rounded-none border-2" />
      </div>
      {isSupplier && (
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Address</Label>
          <Textarea data-testid={`${p}${kind}-address`} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="rounded-none border-2" />
        </div>
      )}
      {!isSupplier && (
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-[0.2em] font-bold">Account number</Label>
          <Input data-testid={`${p}${kind}-account`} value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="e.g. FastCo #A-12345" className="h-10 rounded-none border-2 font-mono" />
        </div>
      )}
    </>
  );
}

function EmailList({ value }) {
  const parts = String(value || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {parts.map((e) => (
        <span key={e} className="font-mono text-xs px-2 py-0.5 border border-border bg-muted/40">{e}</span>
      ))}
    </div>
  );
}

export default function DirectoryPage({ kind }) {
  const isSupplier = kind === "supplier";
  const endpoint = isSupplier ? "/suppliers" : "/couriers";
  const idKey = isSupplier ? "supplier_id" : "courier_id";
  const label = isSupplier ? "Supplier" : "Courier";

  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // full row being edited
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(endpoint);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const buildPayload = (src) => {
    const p = { ...src };
    if (!isSupplier) delete p.address;
    if (isSupplier) delete p.account_number;
    return p;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(endpoint, buildPayload(form));
      toast.success(`${label} added`);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error("Failed", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`${endpoint}/${id}`);
    toast.success(`${label} removed`);
    load();
  };

  const openEdit = (it) => {
    setEditing(it);
    setEditForm({
      name: it.name || "",
      contact_name: it.contact_name || "",
      email: it.email || "",
      phone: it.phone || "",
      address: it.address || "",
      account_number: it.account_number || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      await api.put(`${endpoint}/${editing[idKey]}`, buildPayload(editForm));
      toast.success(`${label} updated`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Failed", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="border-2 border-foreground p-2">
          <LogoMark size={40} className="text-foreground" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">
            Out <span className="text-orange-600">&amp;</span> In &middot; Directory
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">{label}s</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <form onSubmit={submit} className="lg:col-span-5 border-2 border-border bg-card p-6 space-y-4 h-fit">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Add new {label.toLowerCase()}</div>
          <DirectoryFields kind={kind} form={form} setForm={setForm} />
          <Button type="submit" disabled={saving} data-testid={`${kind}-submit`} className="w-full h-10 rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-[0.2em] text-xs font-bold">
            <Plus className="h-3.5 w-3.5 mr-2" /> Add {label}
          </Button>
        </form>

        <div className="lg:col-span-7 border-2 border-border bg-card">
          <div className="px-6 py-3 border-b-2 border-border text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Saved ({items.length})</div>
          <div className="divide-y-2 divide-border">
            {items.length === 0 && <div className="p-6 text-sm text-muted-foreground">No {label.toLowerCase()}s yet.</div>}
            {items.map((it) => (
              <div key={it[idKey]} data-testid={`${kind}-row-${it[idKey]}`} className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{it.name}</div>
                  {it.contact_name && <div className="text-xs text-muted-foreground">{it.contact_name}</div>}
                  <EmailList value={it.email} />
                  {it.phone && <div className="font-mono text-xs text-muted-foreground mt-1">{it.phone}</div>}
                  {it.account_number && <div className="font-mono text-xs text-orange-700 dark:text-orange-400 mt-1">Acct: {it.account_number}</div>}
                  {it.address && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{it.address}</div>}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                  <Button variant="outline" size="icon" onClick={() => openEdit(it)} data-testid={`${kind}-edit-${it[idKey]}`} className="rounded-none border-2 h-9 w-9 hover:border-orange-600 hover:text-orange-600">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => remove(it[idKey])} data-testid={`${kind}-delete-${it[idKey]}`} className="rounded-none border-2 h-9 w-9 hover:border-red-600 hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid={`${kind}-edit-dialog`} className="rounded-none border-2 sm:max-w-lg">
          <DialogHeader>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Edit {label.toLowerCase()}</div>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">
              {editing?.name || label}
            </DialogTitle>
            <DialogDescription>
              Add one email or several separated by commas — every recipient gets a copy of new requests.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 pt-2">
            <DirectoryFields kind={kind} form={editForm} setForm={setEditForm} prefix="edit" />
            <DialogFooter className="pt-2 gap-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-none border-2 h-10 uppercase tracking-[0.15em] text-xs font-bold flex-1">
                <X className="h-3.5 w-3.5 mr-2" /> Cancel
              </Button>
              <Button type="submit" disabled={editSaving} data-testid={`${kind}-edit-save`} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white h-10 uppercase tracking-[0.15em] text-xs font-bold flex-1">
                <Save className="h-3.5 w-3.5 mr-2" /> {editSaving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
